<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import { apiErrorMessage } from '../../../../api/coLiving/errors';
import { SELECT_CLASS } from '../constants';
import FormField from './FormField.vue';

const MEMBER_LIMIT = 100;

// Assign (from Needs admin) or reassign a ticket. Picking a member first asks
// the server what the change would do to the ticket's visits and offers; the
// numbers are shown before the admin confirms.
export default {
  name: 'AssignModal',
  components: { BaseModal, FormField },
  props: {
    ticket: { type: Object, required: true },
  },
  emits: ['close', 'done'],
  data() {
    return {
      selectClass: SELECT_CLASS,
      members: [],
      memberId: '',
      preview: null,
      isLoading: false,
      isSaving: false,
    };
  },
  computed: {
    isAssign() {
      return this.ticket.status === 'NEEDS_ADMIN';
    },
    options() {
      const currentId = this.ticket.assignee && this.ticket.assignee.id;
      return this.members.filter(member => member.id !== currentId);
    },
    hasVisitEffects() {
      const p = this.preview;
      return (
        !!p &&
        (p.visits_moved.length ||
          p.visits_need_reschedule.length ||
          p.offers_withdrawn.length)
      );
    },
  },
  watch: {
    async memberId(id) {
      this.preview = null;
      if (!id) return;
      // Assigning from Needs admin has no visits to move yet, but the call is
      // harmless and keeps one path.
      try {
        this.preview = await this.fetchAssigneePreview({
          id: this.ticket.id,
          memberId: id,
        });
      } catch (err) {
        this.preview = null;
      }
    },
  },
  async mounted() {
    this.isLoading = true;
    try {
      const data = await this.fetchMembers({
        status: 'active',
        page_size: MEMBER_LIMIT,
      });
      this.members = data.results;
    } catch (err) {
      useAlert(apiErrorMessage(this, err, 'CO_LIVING.COMMON.LOAD_FAILED'));
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    ...mapActions('coLivingTeam', ['fetchMembers']),
    ...mapActions('coLivingTickets', [
      'fetchAssigneePreview',
      'changeAssignee',
    ]),
    async onConfirm() {
      if (!this.memberId) return;
      this.isSaving = true;
      try {
        const result = await this.changeAssignee({
          id: this.ticket.id,
          memberId: this.memberId,
        });
        this.$emit('done', result);
        this.$emit('close');
      } catch (err) {
        useAlert(apiErrorMessage(this, err, 'CO_LIVING.TICKETS.ASSIGN.FAILED'));
        // A ticket that stopped being open: the buttons are stale.
        this.$emit('done', null);
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <BaseModal @close="$emit('close')">
    <template #title>
      {{
        $t(
          isAssign
            ? 'CO_LIVING.TICKETS.STEPS.ASSIGN'
            : 'CO_LIVING.TICKETS.ASSIGN.REASSIGN'
        )
      }}
    </template>

    <form
      class="grid grid-cols-1 gap-4"
      data-testid="co-living-assign-form"
      @submit.prevent="onConfirm"
    >
      <FormField
        :label="$t('CO_LIVING.TICKETS.ASSIGN.MEMBER')"
        for-id="co-living-assign-member"
        :hint="$t('CO_LIVING.TICKETS.ASSIGN.HINT')"
      >
        <select
          id="co-living-assign-member"
          v-model="memberId"
          :disabled="isLoading"
          :class="selectClass"
          data-testid="co-living-assign-member"
        >
          <option value="" disabled>
            {{ $t('CO_LIVING.TICKETS.ASSIGN.PLACEHOLDER') }}
          </option>
          <option v-for="member in options" :key="member.id" :value="member.id">
            {{ member.name }} ({{ member.role_label }})
          </option>
        </select>
      </FormField>

      <div
        v-if="hasVisitEffects"
        class="p-3 text-sm rounded bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200"
        data-testid="co-living-assign-preview"
      >
        <p class="font-medium">
          {{ $t('CO_LIVING.TICKETS.ASSIGN.PREVIEW_TITLE') }}
        </p>
        <ul class="mt-1 list-disc list-inside">
          <li v-if="preview.visits_moved.length">
            {{
              $t('CO_LIVING.TICKETS.ASSIGN.VISITS_MOVED', {
                count: preview.visits_moved.length,
              })
            }}
          </li>
          <li v-if="preview.visits_need_reschedule.length">
            {{
              $t('CO_LIVING.TICKETS.ASSIGN.VISITS_RESCHEDULE', {
                count: preview.visits_need_reschedule.length,
              })
            }}
          </li>
          <li v-if="preview.offers_withdrawn.length">
            {{
              $t('CO_LIVING.TICKETS.ASSIGN.OFFERS_WITHDRAWN', {
                count: preview.offers_withdrawn.length,
              })
            }}
          </li>
        </ul>
      </div>

      <div class="flex justify-end gap-3">
        <woot-button variant="clear" type="button" @click="$emit('close')">
          {{ $t('CO_LIVING.COMMON.CANCEL') }}
        </woot-button>
        <woot-button
          type="submit"
          :disabled="!memberId"
          :is-loading="isSaving"
          data-testid="co-living-assign-confirm"
        >
          {{
            $t(
              isAssign
                ? 'CO_LIVING.TICKETS.STEPS.ASSIGN'
                : 'CO_LIVING.TICKETS.ASSIGN.REASSIGN'
            )
          }}
        </woot-button>
      </div>
    </form>
  </BaseModal>
</template>
